<script lang="ts">
	import { goto, yyyy } from "./store.svelte";
	import { toast } from "./lib/toast.svelte.ts";
	import { onMount, onDestroy } from "svelte";
	import { fly } from "svelte/transition";
	import Fa from "svelte-fa";
	import { faEye, faFilePdf, faLink } from "@fortawesome/free-solid-svg-icons";

	let summary: any = $state(null);
	let invoices: any[] = $state([]);
	let loading = $state(true);
	let expanded: string | null = $state(null);
	let selectedInvoice: any = $derived(expanded ? invoices.find((i: any) => i.invoice_id === expanded) : null);
	let invBand1: number = $derived(Math.min(selectedInvoice?.user_count ?? 0, 25));
	let invBand2: number = $derived(Math.max((selectedInvoice?.user_count ?? 0) - 25, 0));
	let invStorageOverage: number = $derived(Math.max((selectedInvoice?.storage_gb ?? 0) - 100, 0));
	let sumBand1: number = $derived(Math.min(summary?.user_count ?? 0, 25));
	let sumBand2: number = $derived(Math.max((summary?.user_count ?? 0) - 25, 0));
	let sumStorageOverage: number = $derived(Math.max((summary?.storage_gb ?? 0) - 100, 0));

	const headers = $derived({
		"Content-Type": "application/json",
		Authorization: "Bearer " + yyyy.sesh.logtoken?.token,
	});

	async function fetchSummary() {
		const res = await fetch(yyyy.uri.api + "/rpc/billing_summary", {
			method: "POST",
			headers,
		});
		if (res.ok) {
			summary = await res.json();
		} else {
			toast.error("Failed to load billing summary");
		}
	}

	async function fetchInvoices() {
		const res = await fetch(yyyy.uri.api + "/rpc/billing_invoices", {
			method: "POST",
			headers,
		});
		if (res.ok) {
			invoices = await res.json();
		}
	}

	onMount(async () => {
		if (!yyyy.sesh.tokeninfo?.userinfo?.admin) {
			toast.warning("You must be an admin user to access billing.");
			goto("/livewires");
			return;
		}
		await Promise.all([fetchSummary(), fetchInvoices()]);
		loading = false;
	});

	onDestroy(() => {
		yyyy.logo.time = 877.5;
		yyyy.logo.usage = "wait";
	});

	function formatCurrency(val: number) {
		return "$" + (val ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	}

	function formatDate(d: string) {
		const s = d.includes('T') ? d : d + 'T00:00:00';
		return new Date(s).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
	}

	function copyInvoiceLink(inv: any) {
		const url = `${window.location.origin}/invoice/${inv.view_token}`;
		navigator.clipboard.writeText(url);
		toast.success("Invoice link copied");
	}

	function dueDate(d: string) {
		const s = d.includes('T') ? d : d + 'T00:00:00';
		const dt = new Date(s);
		dt.setDate(dt.getDate() + 14);
		return dt.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
	}

</script>

{#if selectedInvoice}
	<!-- svelte-ignore a11y_click_events_have_key_events -->
	<!-- svelte-ignore a11y_no_static_element_interactions -->
	<div class="invoice-overlay" onclick={() => expanded = null}>
		<div class="invoice-card" onclick={(e) => e.stopPropagation()}>
			<div class="invoice-header">
				<span class="invoice-brand">Livewire</span>
				<span class="invoice-label">INVOICE</span>
			</div>
			<div class="invoice-meta">
				<div>
					<strong class="invoice-company">{selectedInvoice.company_name || summary?.company || ''}</strong>
					{#if selectedInvoice.address}
						<br><span class="invoice-address">{selectedInvoice.address}</span>
					{/if}
				</div>
				<div class="invoice-meta-right">
					<strong>Invoice #</strong> {selectedInvoice.invoice_id}<br>
					<strong>Period</strong> {formatDate(selectedInvoice.period_start)} — {formatDate(selectedInvoice.period_end)}<br>
					<strong>Issued</strong> {formatDate(selectedInvoice.created_at)}<br>
					<strong>Due</strong> {dueDate(selectedInvoice.created_at)}
				</div>
			</div>
			<table class="invoice-table">
				<thead>
					<tr>
						<th class="has-text-left">Item</th>
						<th class="has-text-right">Qty</th>
						<th class="has-text-right">Rate</th>
						<th class="has-text-right">Amount</th>
					</tr>
				</thead>
				<tbody>
					<tr class="total-users-row">
						<td><strong>Total Users: {selectedInvoice.user_count ?? 0}</strong></td>
						<td></td>
						<td></td>
						<td class="has-text-right"><strong>{formatCurrency(selectedInvoice.user_charge)}</strong></td>
					</tr>
					<tr class="band-detail-row">
						<td>&nbsp;&nbsp;Users (1-25)</td>
						<td class="has-text-right">{invBand1}</td>
						<td class="has-text-right">$179/user</td>
						<td class="has-text-right">{formatCurrency(invBand1 * 179)}</td>
					</tr>
					{#if invBand2 > 0}
						<tr class="band-detail-row">
							<td>&nbsp;&nbsp;Users (26-75)</td>
							<td class="has-text-right">{invBand2}</td>
							<td class="has-text-right">$129/user</td>
							<td class="has-text-right">{formatCurrency(invBand2 * 129)}</td>
						</tr>
					{/if}
					<tr>
						<td>Livewires <small>(info only)</small></td>
						<td class="has-text-right">{selectedInvoice.livewire_count ?? ''}</td>
						<td class="has-text-right">—</td>
						<td class="has-text-right">—</td>
					</tr>
					<tr>
						<td>Storage <small>(100 GB included)</small></td>
						<td class="has-text-right">{selectedInvoice.storage_gb != null ? Number(selectedInvoice.storage_gb).toFixed(2) + ' GB' : ''}</td>
						<td class="has-text-right">{invStorageOverage > 0 ? '$5/GB' : 'Free'}</td>
						<td class="has-text-right">{invStorageOverage > 0 ? formatCurrency(selectedInvoice.storage_charge) : '$0.00'}</td>
					</tr>
					{#each selectedInvoice.addon_details ?? [] as ad}
						<tr>
							<td>{ad.name}{#if ad.days_active < ad.total_days} <small>({ad.days_active}/{ad.total_days} days)</small>{/if}</td>
							<td class="has-text-right"></td>
							<td class="has-text-right"></td>
							<td class="has-text-right">{formatCurrency(ad.charge)}</td>
						</tr>
					{/each}
					{#each selectedInvoice.line_items ?? [] as li}
						<tr>
							<td>{li.description}</td>
							<td class="has-text-right"></td>
							<td class="has-text-right"></td>
							<td class="has-text-right">{formatCurrency(li.amount)}</td>
						</tr>
					{/each}
					{#if selectedInvoice.discount > 0}
						<tr class="discount-row">
							<td>Discount</td>
							<td></td>
							<td></td>
							<td class="has-text-right">-{formatCurrency(selectedInvoice.discount)}</td>
						</tr>
					{/if}
				</tbody>
				<tfoot>
					<tr>
						<td>Total</td>
						<td></td>
						<td></td>
						<td class="has-text-right">{formatCurrency(selectedInvoice.total)}</td>
					</tr>
				</tfoot>
			</table>
			<div class="invoice-footer">
				<a href="https://lvwr.io">lvwr.io</a>
				<span class="sep">|</span>
				<a href="mailto:billing@lvwr.io">billing@lvwr.io</a>
			</div>
			</div>
	</div>
{/if}

<div class="column not-too-wide billing-wide">
	<div class="card lvwr-card">
		<div class="card-content lvwr-card-content" transition:fly|local={{ y: -200 }}>
			<h1 class="title thin lvwr-title">Billing</h1>

			{#if loading}
				<p class="has-text-centered">Loading...</p>
			{:else if summary}
				<div class="content lvwr-content">
					<div class="billing-section">
						<div class="billing-header">
							<span class="billing-company">{summary.company}</span>
							<span class="billing-cycle tag" class:is-success={summary.billing_cycle === 'annual'}>
								{summary.billing_cycle}
							</span>
						</div>

						<h2 class="subtitle is-5">Current Usage</h2>
						<table class="table is-fullwidth is-hoverable">
							<thead>
								<tr>
									<th>Item</th>
									<th class="has-text-right">Qty</th>
									<th class="has-text-right">Rate</th>
									<th class="has-text-right">Charge</th>
								</tr>
							</thead>
							<tbody>
								<tr class="total-users-row">
									<td><strong>Total Users: {summary.user_count}</strong></td>
									<td></td>
									<td></td>
									<td class="has-text-right"><strong>{formatCurrency(summary.user_charge)}</strong></td>
								</tr>
								<tr class="band-detail-row">
									<td>&nbsp;&nbsp;Users (1-25)</td>
									<td class="has-text-right">{sumBand1}</td>
									<td class="has-text-right">$179/user</td>
									<td class="has-text-right">{formatCurrency(sumBand1 * 179)}</td>
								</tr>
								{#if sumBand2 > 0}
									<tr class="band-detail-row">
										<td>Users (26-75)</td>
										<td class="has-text-right">{sumBand2}</td>
										<td class="has-text-right">$129/user</td>
										<td class="has-text-right">{formatCurrency(sumBand2 * 129)}</td>
									</tr>
								{/if}
								<tr>
									<td>Livewires <small>(info only)</small></td>
									<td class="has-text-right">{summary.livewire_count}</td>
									<td class="has-text-right">—</td>
									<td class="has-text-right">—</td>
								</tr>
								<tr>
									<td>Storage <small>(100 GB included)</small></td>
									<td class="has-text-right">{summary.storage_gb?.toFixed(2)} GB</td>
									<td class="has-text-right">{sumStorageOverage > 0 ? '$5/GB' : 'Free'}</td>
									<td class="has-text-right">{sumStorageOverage > 0 ? formatCurrency(summary.storage_charge) : '$0.00'}</td>
								</tr>
							</tbody>
						</table>

						{#if summary.active_addons?.length > 0}
							<h2 class="subtitle is-5">Active Add-ons</h2>
							<table class="table is-fullwidth is-hoverable">
								<thead>
									<tr>
										<th>Add-on</th>
										<th class="has-text-right">Monthly</th>
									</tr>
								</thead>
								<tbody>
									{#each summary.active_addons as addon}
										<tr>
											<td>{addon.name}</td>
											<td class="has-text-right">{formatCurrency(addon.price)}</td>
										</tr>
									{/each}
								</tbody>
							</table>
						{/if}

						<div class="billing-totals">
							<div class="billing-row">
								<span>Add-on Total</span>
								<span>{formatCurrency(summary.addon_charge)}</span>
							</div>
							{#if summary.discount > 0}
								<div class="billing-row discount">
									<span>Annual Discount</span>
									<span>-{formatCurrency(summary.discount)}</span>
								</div>
							{/if}
							<div class="billing-row total">
								<span>Projected Monthly Total</span>
								<span>{formatCurrency(summary.total)}</span>
							</div>
						</div>
					</div>

					{#if invoices.length > 0}
						<div class="billing-section">
							<h2 class="subtitle is-5">Invoice History</h2>
							<table class="table is-fullwidth is-hoverable is-striped">
								<thead>
									<tr>
										<th>Invoice</th>
										<th>Period</th>
										<th class="has-text-right">Total</th>
										<th class="has-text-right">Created</th>
										<th></th>
									</tr>
								</thead>
								<tbody>
									{#each invoices as inv}
										<tr>
											<td><code>{inv.invoice_id}</code></td>
											<td>{formatDate(inv.period_start)} — {formatDate(inv.period_end)}</td>
											<td class="has-text-right">{formatCurrency(inv.total)}</td>
											<td class="has-text-right">{formatDate(inv.created_at)}</td>
											<td class="has-text-right">
												<button class="button is-small is-link is-outlined" onclick={() => expanded = inv.invoice_id}><Fa icon={faEye} />&nbsp;View</button>
												<button class="button is-small is-link is-outlined" onclick={() => window.open(`/invoice/${inv.view_token}?download`, '_blank')}><Fa icon={faFilePdf} />&nbsp;PDF</button>
												<button class="button is-small is-link is-outlined" onclick={() => copyInvoiceLink(inv)}><Fa icon={faLink} />&nbsp;Link</button>
											</td>
										</tr>
									{/each}
								</tbody>
							</table>
						</div>
					{:else}
						<p class="has-text-centered has-text-grey">No invoices generated yet.</p>
					{/if}
				</div>
			{/if}
		</div>

		<footer class="card-footer" transition:fly|local={{ y: 200 }}>
			<div class="card-footer-item">
				<button class="button lvwr-toggle" onclick={() => goto("/livewires")}>Back</button>
			</div>
		</footer>
	</div>
</div>

<style>
	.billing-wide {
		max-width: 50vw;
	}

	.billing-section {
		margin-bottom: 2rem;
	}

	.billing-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 1.5rem;
	}

	.billing-company {
		font-size: 1.2rem;
		font-weight: 600;
		color: #363636;
	}

	.billing-cycle {
		text-transform: capitalize;
	}

	.billing-totals {
		border-top: 2px solid #eee;
		padding-top: 1rem;
		margin-top: 1rem;
	}

	.billing-row {
		display: flex;
		justify-content: space-between;
		padding: 0.4rem 0;
		font-size: 0.95rem;
	}

	.billing-row.discount {
		color: #48c774;
	}

	.billing-row.total {
		font-weight: 700;
		font-size: 1.1rem;
		border-top: 1px solid #dbdbdb;
		padding-top: 0.75rem;
		margin-top: 0.25rem;
	}

	.invoice-overlay {
		position: fixed;
		inset: 0;
		background: rgba(0, 0, 0, 0.5);
		z-index: 20;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.invoice-card {
		background: #fff;
		border-radius: 8px;
		width: 30%;
		min-width: 800px;
		max-height: 90vh;
		overflow-y: auto;
		font-family: Arial, Helvetica, sans-serif;
		font-size: 15px;
		line-height: 1.6;
		color: #333;
	}

	.invoice-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 24px 30px;
		border-bottom: 1px solid #eee;
	}

	.invoice-brand {
		color: #ff3e00;
		font-family: Ubuntu, Inter, SF Pro, Segoe UI, Roboto, Helvetica Neue, Helvetica, Arial, sans-serif;
		font-size: 24px;
		font-weight: 300;
		text-transform: uppercase;
		letter-spacing: 2px;
	}

	.invoice-label {
		font-size: 13px;
		color: #999;
	}

	.invoice-meta {
		display: flex;
		justify-content: space-between;
		padding: 30px 30px 0 30px;
	}

	.invoice-company {
		font-size: 16px;
	}

	.invoice-address {
		color: #666;
		font-size: 13px;
		white-space: pre-line;
	}

	.invoice-meta-right {
		text-align: right;
		font-size: 13px;
		color: #666;
	}

	.invoice-meta-right strong {
		color: #333;
	}

	.invoice-table {
		width: 100%;
		border-collapse: collapse;
		margin: 24px 0 0 0;
		padding: 0 30px;
	}

	.invoice-table thead tr {
		border-bottom: 2px solid #eee;
	}

	.invoice-table th {
		padding: 8px 30px;
		font-size: 13px;
		text-transform: uppercase;
		color: #999;
		letter-spacing: 0.5px;
		font-weight: 600;
	}

	.invoice-table tbody td {
		padding: 8px 30px;
		border-bottom: 1px solid #f0f0f0;
	}

	.invoice-table .discount-row td {
		color: #48c774;
	}

	.invoice-table tfoot td {
		padding: 12px 30px;
		font-weight: 700;
		font-size: 16px;
		border-top: 2px solid #eee;
	}

	.invoice-footer {
		padding: 20px 30px;
		background: #f9f9f9;
		border-top: 1px solid #eee;
		text-align: center;
		font-size: 14px;
	}

	.invoice-footer a {
		color: #ff3e00;
		text-decoration: none;
	}

	.invoice-footer a:last-child {
		color: #666;
	}

	.invoice-footer .sep {
		color: #ccc;
		margin: 0 10px;
	}

	code {
		font-size: 0.85rem;
		background: #f5f5f5;
		padding: 2px 6px;
		border-radius: 3px;
	}

	:global(.band-detail-row td) {
		color: #888 !important;
		font-size: 0.85em;
	}

</style>
